import { AlertTriangle, ShieldAlert, ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { failureSentence } from '../types'
import type { CredentialCheckView, CredentialFailureView } from '../types'

export interface CredentialPanelProps {
  check: CredentialCheckView | undefined
  loading?: boolean
  /** Rendered as the action on a blocking failure; omitted where nobody may override. */
  onOverride?: (failure: CredentialFailureView) => void
  canOverride?: boolean
}

/**
 * What the credential rules say about a placement, before it is made.
 *
 * <p>Blocking failures and warnings are visually distinct and never merged into one list.
 * They mean different things — one stops the move, the other does not — and a single list of
 * amber rows teaches people to skim past the ones that matter.
 *
 * <p>Silence when everything is fine is deliberate: a green "no problems" panel on every
 * transfer is furniture, and furniture is what people stop reading.
 */
export function CredentialPanel({ check, loading, onOverride, canOverride }: CredentialPanelProps) {
  if (loading) {
    return (
      <p className="text-sm text-[var(--color-muted)]" role="status">
        Checking documents…
      </p>
    )
  }
  if (!check) return null

  const nothingToSay = check.blocking.length === 0 && check.warnings.length === 0
  if (nothingToSay) {
    return (
      <p className="flex items-center gap-2 text-sm text-[var(--color-success)]">
        <ShieldCheck className="size-4" aria-hidden="true" />
        Every document this placement requires is valid.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {check.blocking.length > 0 && (
        <section
          aria-label="Blocking credential failures"
          className="rounded-[var(--radius-card)] border border-[var(--color-danger)] bg-[var(--color-tint-red)] p-3"
        >
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-danger)]">
            <ShieldAlert className="size-4" aria-hidden="true" />
            This placement is blocked
          </h3>
          <ul className="mt-2 space-y-2">
            {check.blocking.map((failure) => (
              <FailureRow
                key={failure.ruleId}
                failure={failure}
                tone="danger"
                action={
                  canOverride && onOverride ? (
                    <Button type="button" size="sm" variant="outline" onClick={() => onOverride(failure)}>
                      Override with a reason
                    </Button>
                  ) : undefined
                }
              />
            ))}
          </ul>
          {!canOverride && (
            <p className="mt-2 text-xs text-[var(--color-danger)]">
              Fix the document, or ask somebody who holds the compliance override permission.
            </p>
          )}
        </section>
      )}

      {check.warnings.length > 0 && (
        <section
          aria-label="Credential warnings"
          className="rounded-[var(--radius-card)] border border-[var(--color-warning)] bg-[var(--color-tint-orange)] p-3"
        >
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-warning)]">
            <AlertTriangle className="size-4" aria-hidden="true" />
            Worth knowing, but not blocking
          </h3>
          <ul className="mt-2 space-y-2">
            {check.warnings.map((failure) => (
              <FailureRow key={failure.ruleId} failure={failure} tone="warning" />
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function FailureRow({
  failure,
  tone,
  action,
}: {
  failure: CredentialFailureView
  tone: 'danger' | 'warning'
  action?: React.ReactNode
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm">
        <span className={cn('font-medium', tone === 'danger' ? 'text-[var(--color-danger)]' : 'text-[var(--color-warning)]')}>
          {failure.documentTypeName}
        </span>
        <span className="text-[var(--color-body)]"> — {failureSentence(failure)}</span>
        <span className="text-[var(--color-muted)]"> ({failure.ruleName})</span>
        {failure.overridden && (
          <span className="ms-2 rounded-full bg-white/60 px-2 py-0.5 text-xs font-medium">
            overridden
          </span>
        )}
      </span>
      {action}
    </li>
  )
}
